import { PartnerAffiliation } from '@/@types/PartnerProgram';
import { usePermissions } from '@/auth/PermissionsContext';
import Divider from '@/components/shared/Divider/Divider';
import Loader from '@/components/Loader/Loader';
import Badge from '@/components/shared/Badge/Badge';
import { Input } from '@/components/shared/Input/Input';
import CTATable from '@/components/shared/TableListCustom/CTATable';
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { TableCell, TableRow } from '@/components/ui/table';
import { toast } from '@/hooks/use-toast';
import useModalStore from '@/store/useModalStore';
import useCurrencyStore from '@/store/useCurrencyStore';
import { formatCurrencyFromCents } from '@/lib/select-options';
import { Copy, Loader2, Plus, Settings2, Trash2, Users, Percent } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useAttachPartner, useDeactivatePartner } from '../../Affiliate/hooks/useManagePartner';
import { useConfigurePartnerProgram } from '../../Affiliate/hooks/useConfigurePartnerProgram';
import { useFetchPartnerProgram } from '../../Affiliate/hooks/useFetchPartnerProgram';
import { useFetchPartners } from '../../Affiliate/hooks/useFetchPartners';
import ConfigurePartnerCommissionModal from './ConfigurePartnerCommissionModal';

interface Props {
  productId: string;
}

export default function PartnersList({ productId }: Props) {
  const { t } = useTranslation(['affiliates', 'common']);
  const { openModal } = useModalStore();
  const { hasPermission } = usePermissions();

  const canConfigureProgram = hasPermission(
    'partner_indications.configure_partner_program',
  );
  const canViewPartners = hasPermission(
    'partner_indications.view_partner_indications',
  );
  const canManagePartners = hasPermission('partner_indications.manage_partners');
  const currency = useCurrencyStore((s) => s.currency);

  const { partnerProgram, isLoading: isLoadingProgram } =
    useFetchPartnerProgram(productId);
  const { configurePartnerProgram } = useConfigurePartnerProgram(productId);
  const isEnabled = partnerProgram?.is_enabled ?? false;
  const { partnersData, isLoading: isLoadingPartners, page, setPage } =
    useFetchPartners(productId, isEnabled && canViewPartners);
  const { mutate: attachPartner, isPending: isAttaching } =
    useAttachPartner(productId);
  const { mutate: deactivatePartner, isPending: isDeactivating } =
    useDeactivatePartner(productId);

  const [addPartnerOpen, setAddPartnerOpen] = useState(false);
  const [partnerEmail, setPartnerEmail] = useState('');
  const [partnerEmailError, setPartnerEmailError] = useState('');
  const [removeTarget, setRemoveTarget] = useState<PartnerAffiliation | null>(null);
  const [commissionTarget, setCommissionTarget] = useState<PartnerAffiliation | null>(null);

  const { handleSubmit, setValue, watch, reset } = useForm({
    defaultValues: {
      is_enabled: false,
    },
  });

  const isProgramEnabled = watch('is_enabled');

  useEffect(() => {
    if (partnerProgram) {
      reset({ is_enabled: partnerProgram.is_enabled });
    }
  }, [partnerProgram, reset]);

  const handleSwitchChange = (checked: boolean) => {
    if (!canConfigureProgram) return;

    setValue('is_enabled', checked);
    handleSubmit(async () => {
      await configurePartnerProgram({
        is_enabled: checked,
        commission_type: partnerProgram?.commission_type || 'fixed',
        commission_value: partnerProgram?.commission_value || 500,
        commission_currency: partnerProgram?.commission_currency || 'BRL',
        is_lifetime: partnerProgram?.is_lifetime ?? true,
        duration_days: partnerProgram?.duration_days ?? null,
      });
    })();
  };

  const handleCopyPartnerInviteLink = async () => {
    try {
      const link = `${window.location.origin}/invitation-partner/${productId}`;
      await navigator.clipboard.writeText(link);
      toast({
        variant: 'success',
        description: t('affiliates:partner_invite_link_copied'),
      });
    } catch {
      toast({
        variant: 'error',
        description: t('common:link_copy_error'),
      });
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddPartner = () => {
    const trimmedEmail = partnerEmail.trim().toLowerCase();
    if (!trimmedEmail) return;

    if (!validateEmail(trimmedEmail)) {
      setPartnerEmailError(t('affiliates:invalid_email'));
      return;
    }

    attachPartner(trimmedEmail, {
      onSuccess: () => {
        setPartnerEmail('');
        setAddPartnerOpen(false);
      },
    });
  };

  const handleRemovePartner = (affiliation: PartnerAffiliation) => {
    deactivatePartner(affiliation.partner?.id ?? '', {
      onSuccess: () => setRemoveTarget(null),
    });
  };

  if (isLoadingProgram) {
    return (
      <Card className="mt-8 p-6">
        <div className="flex items-center justify-center h-32">
          <Loader />
        </div>
      </Card>
    );
  }

  const headers = [
    { title: t('affiliates:partner_name'), key: 'partner_name' },
    { title: t('affiliates:commission_rate', 'Taxa'), key: 'commission_rate' },
    { title: t('affiliates:commissions_count'), key: 'commissions_count' },
    { title: t('affiliates:total_volume'), key: 'total_volume' },
    { title: t('common:status'), key: 'status' },
    ...(canManagePartners ? [{ title: '', key: 'actions' }] : []),
  ];

  const partners = partnersData?.data ?? [];

  const getPartnerStatus = (partner: PartnerAffiliation) => {
    if (partner.deactivated_at) return 'disabled';
    if (partner.accepted_at) return 'active';
    return 'pending';
  };

  const getPartnerStatusLabel = (partner: PartnerAffiliation) => {
    if (partner.deactivated_at) return t('affiliates:deactivated');
    if (partner.accepted_at) return t('affiliates:linked');
    return t('affiliates:awaiting');
  };

  const getCommissionRateText = (partner: PartnerAffiliation) => {
    const isSpecific = !!partner.commission?.value;
    const type = isSpecific ? partner.commission!.type : partnerProgram?.commission_type;
    const value = isSpecific ? partner.commission!.value : partnerProgram?.commission_value;
    const curr = isSpecific ? partner.commission!.currency : partnerProgram?.commission_currency;

    if (!type || value == null) return '-';

    if (type === 'percentage') {
      return `${value}%`;
    }
    
    return formatCurrencyFromCents(value, curr || currency);
  };

  const showActions = canConfigureProgram || canManagePartners;

  return (
    <>
      <Card className="mt-8 w-full overflow-x-auto">
        <div className="p-4 flex flex-col md:flex-row md:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <h1 className="text-base font-semibold">
              {t('affiliates:partner_program_title')}
            </h1>
            {canConfigureProgram && (
              <Badge status={isProgramEnabled ? 'enabled' : 'disabled'} />
            )}
          </div>
          {canConfigureProgram && (
            <div className="flex items-center gap-2">
              <Switch
                checked={isProgramEnabled}
                onCheckedChange={handleSwitchChange}
                data-cy="switch-partner-program"
              />
            </div>
          )}
        </div>

        {isProgramEnabled && (
          <>
            <Divider className="mt-[-4px]" />
            {showActions && (
              <div className="flex flex-row max-sm:flex-col gap-2 w-full p-4">
                 {canManagePartners && (
                  <Button
                    variant="outline"
                    onClick={handleCopyPartnerInviteLink}
                    className="w-full flex-1 h-10 min-w-0"
                    data-cy="btn-copy-partner-invite-link"
                  >
                    <Copy className="mr-2 h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {t('affiliates:partner_invite_link_title')}
                    </span>
                  </Button>
                )}
                {canManagePartners && (
                  <Button
                    variant="outline"
                    onClick={() => setAddPartnerOpen(true)}
                    className="w-full flex-1 h-10 min-w-0"
                    data-cy="btn-add-partner"
                  >
                    <Plus className="mr-2 h-4 w-4 shrink-0" />
                    <span className="truncate">{t('affiliates:add_partner')}</span>
                  </Button>
                )}
                {canConfigureProgram && (
                  <Button
                    onClick={() => openModal('config-partner-program')}
                    className="w-full flex-1 h-10 min-w-0"
                    data-cy="btn-open-partner-config"
                  >
                    <Settings2 className="mr-2 h-4 w-4 shrink-0" />
                    <span className="truncate">
                      {t('affiliates:configure_program')}
                    </span>
                  </Button>
                )}
              </div>
            )}

            {canViewPartners && (
              <TableListCustom
                emptyTitleMessage={t('affiliates:no_partners_title')}
                emptyDescriptionMessage={t('affiliates:no_partners_description')}
                emptyIconMessage={<Users />}
                headers={headers}
                isLoadingData={isLoadingPartners}
                data={partnersData}
                currentPage={page}
                setCurrentPage={setPage}
              >
                {partners.map((partner) => (
                  <TableRow key={partner.id} className="hover:bg-transparent">
                    <TableCell>
                      <div className="flex flex-col">
                        <span className="text-sm font-medium text-white">
                          {partner.partner?.name}
                        </span>
                        <span className="text-xs text-gray-400">
                          {partner.partner?.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium">
                        {getCommissionRateText(partner)}
                      </span>
                    </TableCell>
                    <TableCell>
                      {partner.metrics?.commissions_count ?? 0}
                    </TableCell>
                    <TableCell>
                      {formatCurrencyFromCents(
                        partner.metrics?.commissions_total_converted ?? 0,
                        currency,
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge
                        status={getPartnerStatus(partner)}
                        text={getPartnerStatusLabel(partner)}
                      />
                    </TableCell>
                    {canManagePartners && (
                      <TableCell className="w-[50px]">
                        <CTATable
                          items={[
                            {
                              id: 1,
                              label: t('affiliates:configure_commission', 'Configurar Comissão'),
                              icon: <Percent size={16} />,
                              action: () => setCommissionTarget(partner),
                            },
                            {
                              id: 2,
                              label: t('affiliates:remove_partner'),
                              icon: <Trash2 size={16} />,
                              action: () => setRemoveTarget(partner),
                            },
                          ]}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableListCustom>
            )}
          </>
        )}
      </Card>

      {canManagePartners && (
        <>
          <Dialog
            open={addPartnerOpen}
            onOpenChange={(open) => {
              setAddPartnerOpen(open);
              if (!open) {
                setPartnerEmail('');
                setPartnerEmailError('');
              }
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('affiliates:add_partner')}</DialogTitle>
                <DialogDescription>
                  {t('affiliates:add_partner_description')}
                </DialogDescription>
              </DialogHeader>
              <Input
                label={t('affiliates:partner_email_label')}
                placeholder={t('affiliates:partner_email_placeholder')}
                value={partnerEmail}
                onChange={(e) => {
                  setPartnerEmail(e.target.value);
                  if (partnerEmailError) setPartnerEmailError('');
                }}
                helperText={partnerEmailError}
                type="email"
                data-cy="input-partner-email"
              />
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="ghost">{t('common:cancel')}</Button>
                </DialogClose>
                <Button
                  onClick={handleAddPartner}
                  disabled={isAttaching || !partnerEmail.trim()}
                  data-cy="btn-confirm-add-partner"
                >
                  {isAttaching ? (
                    <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  ) : null}
                  {t('affiliates:add')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Dialog
            open={removeTarget !== null}
            onOpenChange={(open) => !open && setRemoveTarget(null)}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('affiliates:remove_partner_title')}</DialogTitle>
                <DialogDescription>
                  {t('affiliates:remove_partner_description')}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <DialogClose asChild>
                  <Button variant="ghost">{t('common:cancel')}</Button>
                </DialogClose>
                <Button
                  className="bg-red-500 hover:bg-red-600"
                  onClick={() => removeTarget && handleRemovePartner(removeTarget)}
                  disabled={isDeactivating}
                >
                  {isDeactivating
                    ? t('affiliates:removing')
                    : t('affiliates:confirm_remove')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <ConfigurePartnerCommissionModal
            productId={productId}
            partner={commissionTarget}
            isOpen={commissionTarget !== null}
            onClose={() => setCommissionTarget(null)}
          />
        </>
      )}
    </>
  );
}
